import { useState } from "react";
import { useWindowSize } from "hooks/useWindowSize";
import { Search } from "components/DesignSystem/Search/Search";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { parse, stringify } from "qs";
import { debounce } from "utils/debouncing";
import { useUpdateQuery } from "hooks/useQuery";
import { useFilters } from "hooks/useFilter";

import Loader from "components/DesignSystem/Loader/loader";
import Table from "components/DesignSystem/Table/V2/Table";

import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useQuery } from "hooks/useQuery";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";

import {
  StateRegistrationsRes,
  useGetStateRegistrationsQuery,
  useGetStateRegistrationSuggestionsQuery,
  useAddStateRegistrationMutation,
  useSubmitRegistrationIntakeMutation,
  FilingDeadlineFilter,
} from "store/apis/StateRegistrations";
import { BillingInvoice } from "types/Models/billing";
import { Cart } from "store/apis/products";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { useToast } from "hooks/useToast";

import { AddStateRegistrationModal } from "./AddStateRegistrationModal";
import { AddStateDropdown } from "./AddStateDropdown";
import { RegistrationOnboarding } from "./RegistrationOnboarding";
import { StateRegistrationActionCards } from "./StateRegistrationActionCards";
import { CheckoutModal } from "components/CheckoutModal/CheckoutModal";
import {
  isFqDateAttr,
  isSosNumberAttr,
  MISSING_FQ_DATE_LABEL,
  MISSING_SOS_NUMBER_LABEL,
  RegistrationIntent,
} from "./constants";
import { MissingInfoModal } from "./MissingInfoModal";
import { useGetEntityDataAttributesQuery } from "store/apis/entity";
import { useRegistrationCheckout } from "./useRegistrationCheckout";
import { useStateDeadlineTasks } from "./useStateDeadlineTasks";
import { NextDeadlineSection } from "./NextDeadlineSection";
import { StateTableRow } from "./StateTableRow";
import { SuggestionsBanner } from "./SuggestionsBanner";
import {
  StateComplianceFilter,
  StateComplianceFilterType,
} from "./StateComplianceFilter";

const EMPTY_STATE_COMPLIANCE_FILTER: StateComplianceFilterType = {
  STATUS: [],
  DEADLINE: [],
};

// ─── Main ─────────────────────────────────────────────────────────────────────

export const StateRegistrations = () => {
  const { uuid: groupId, secondary_channel_url: generalHelpChannelUrl } =
    useCurrentGroupContext();
  const { isMobile } = useWindowSize();

  // The cart opens in a new tab on desktop; on mobile it stays in the same tab.
  const openCart = (cartUrl: string) => {
    if (isMobile) {
      history.push(cartUrl);
      return;
    }

    window.open(cartUrl, "_blank");
  };
  const query = useQuery();
  const entityId = query.get("entity");
  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();
  const parsedSearch = parse(location.search, { ignoreQueryPrefix: true });
  const searchTerm = (parsedSearch.search_term as string) || "";

  const { update } = useUpdateQuery();

  const { values: complianceFilter, updateFilter: updateComplianceFilter } =
    useFilters<StateComplianceFilterType>({
      initialValue: EMPTY_STATE_COMPLIANCE_FILTER,
      useQueryParams: true,
    });

  const selectedDeadlineFilters = complianceFilter.DEADLINE || [];
  const showOverdueOnly = selectedDeadlineFilters.includes(
    FilingDeadlineFilter.OVERDUE
  );
  const showDueOnly = selectedDeadlineFilters.includes(
    FilingDeadlineFilter.DUE
  );

  const {
    data: stateRegistrations = [],
    isLoading,
    isSuccess,
    refetch: refetchStateRegistrations,
  } = useGetStateRegistrationsQuery(
    {
      groupId: groupId!,
      entityId: entityId!,
      searchTerm: searchTerm || undefined,
      statuses: complianceFilter.STATUS?.length
        ? complianceFilter.STATUS
        : undefined,
      deadlineFilters: selectedDeadlineFilters.length
        ? selectedDeadlineFilters
        : undefined,
    },
    { skip: !entityId || !groupId, refetchOnMountOrArgChange: true }
  );

  const { data: suggestions = [] } = useGetStateRegistrationSuggestionsQuery(
    { groupId: groupId!, entityId: entityId! },
    { skip: !entityId || !groupId }
  );

  const { data: entityAttributes = [] } = useGetEntityDataAttributesQuery(
    { groupId: groupId!, entityId: entityId! },
    { skip: !entityId || !groupId }
  );

  const missingInfoModal = useModal();
  const [missingInfoRow, setMissingInfoRow] =
    useState<StateRegistrationsRes | null>(null);
  const addStateModal = useModal();
  const [addStateIntent, setAddStateIntent] =
    useState<RegistrationIntent | null>(null);
  const [inkleHandleState, setInkleHandleState] =
    useState<StateRegistrationsRes | null>(null);
  const inkleHandleModal = useModal();
  const [rowInkleHandleState, setRowInkleHandleState] =
    useState<StateRegistrationsRes | null>(null);
  const rowInkleHandleModal = useModal();
  const [moveToInkleLoadingStateId, setMoveToInkleLoadingStateId] = useState<
    string | null
  >(null);
  const [addStateRegistration] = useAddStateRegistrationMutation();
  const [submitIntake] = useSubmitRegistrationIntakeMutation();
  const { data: subscriptions = [] } = useGetSubscriptionsQuery(
    { groupId: groupId!, entityId: entityId! },
    { skip: !groupId || !entityId }
  );
  const { alertToast, successToast } = useToast();

  // debounce returns a stable reference — update is stable from useUpdateQuery
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchChange = debounce((value: string) => {
    update({ query: "search_term", value: value || null });
  });

  const preservedSearch = stringify(
    { entity: parsedSearch.entity, company: parsedSearch.company },
    { skipNulls: true, addQueryPrefix: true }
  );

  const handleReviewOverdueFilings = () => {
    updateComplianceFilter("DEADLINE", [FilingDeadlineFilter.OVERDUE]);
  };

  const handleReviewDueFilings = () => {
    updateComplianceFilter("DEADLINE", [FilingDeadlineFilter.DUE]);
  };

  const navigateToState = (stateId: string) => {
    history.push(`${url}/${stateId}${preservedSearch}`);
  };

  const { isForeignCA, isAdmin } = useRoleBasedView();

  const handleMoveToInkle = async (row: StateRegistrationsRes) => {
    const raSubscription = subscriptions.find(
      (s) => s.subscription_type === "REGISTERED_AGENT"
    );

    if (!raSubscription || !groupId || !entityId) {
      setRowInkleHandleState(row);
      rowInkleHandleModal.open();
      return;
    }

    try {
      setMoveToInkleLoadingStateId(row.state_id);
      await submitIntake({
        groupId,
        entityId,
        stateId: row.state_id,
        intakeEntries: [{ type: "REGISTERED_AGENT", fields: {} }],
      }).unwrap();

      successToast({ message: "Registered Agent added to cart" });

      const cartUrl = isAdmin
        ? `/admin/crm/${groupId}/billing/cart?entity=${entityId}`
        : `/settings/billing/cart?entity=${entityId}`;
      openCart(cartUrl);
    } catch (err) {
      alertToast({
        message:
          (err as { data?: { message?: string } })?.data?.message ??
          "Something went wrong",
      });
    } finally {
      setMoveToInkleLoadingStateId(null);
    }
  };

  const { getDeadlineForState } = useStateDeadlineTasks();

  const {
    invoice,
    setInvoice,
    checkoutModal,
    handleFileNow,
    closeCheckoutModal,
    onPaymentSuccess,
    checkingOut,
  } = useRegistrationCheckout();

  const handleIntakeComplete = (
    submittedCart: Cart,
    submittedInvoice: BillingInvoice | null
  ) => {
    if (!submittedCart?.uuid || !submittedInvoice) return;
    setInvoice(submittedInvoice);
    checkoutModal.open();
  };

  const handleDeadlineClick = (task: Parameters<typeof handleFileNow>[0]) => {
    if (task.task_uuid) {
      history.push(`/tax/filings/${task.task_uuid}`);
      return;
    }
    void handleFileNow(task);
  };

  const handleStartFiling = (task: Parameters<typeof handleFileNow>[0]) => {
    void handleFileNow(task);
  };

  if (isLoading || !isSuccess) {
    return <Loader />;
  }

  const handleStartRegistration = async (stateId: string) => {
    let stateData = stateRegistrations.find((s) => s.state_id === stateId);
    if (!stateData) {
      await addStateRegistration({
        groupId: groupId!,
        entityId: entityId!,
        stateId,
      }).unwrap();
      const fresh = await refetchStateRegistrations().unwrap();
      stateData = fresh.find((s) => s.state_id === stateId);
    }
    if (stateData) {
      setInkleHandleState(stateData);
      inkleHandleModal.open();
    }
  };

  const handleViewCart = () => {
    const cartUrl = isAdmin
      ? `/admin/crm/${groupId}/billing/cart?entity=${entityId}`
      : `/settings/billing/cart?entity=${entityId}`;
    openCart(cartUrl);
  };

  const getMissingFieldsForRow = (row: StateRegistrationsRes) => {
    // Which fields count as missing is decided once, on the backend
    // (row.compliance_status.missing_field_labels) — this only resolves those
    // labels to the actual editable attribute objects the modal needs.
    const missingLabels = new Set(row.compliance_status.missing_field_labels);
    const fields = entityAttributes.filter(
      (attr) => !attr.value && missingLabels.has(attr.label ?? attr.name)
    );

    const sosAttrs = row.SECRETARY_OF_STATE.data_attributes;

    if (!row.is_home_state && missingLabels.has(MISSING_FQ_DATE_LABEL)) {
      const fqAttr = sosAttrs.find((a) => isFqDateAttr(a) && !a.value);
      if (fqAttr) {
        fields.push({
          name: fqAttr.field_key,
          label: MISSING_FQ_DATE_LABEL,
          value: null,
          isStateField: true,
        } as any);
      }
    }

    if (missingLabels.has(MISSING_SOS_NUMBER_LABEL)) {
      const sosNumAttr = sosAttrs.find((a) => isSosNumberAttr(a) && !a.value);
      if (sosNumAttr) {
        fields.push({
          name: sosNumAttr.field_key,
          label: MISSING_SOS_NUMBER_LABEL,
          value: null,
          isStateField: true,
        } as any);
      }
    }

    return fields;
  };

  const handleSelectAddStateIntent = (selectedIntent: RegistrationIntent) => {
    setAddStateIntent(selectedIntent);
    addStateModal.open();
  };

  return (
    <div className="t-flex t-flex-col t-gap-6">
      <SuggestionsBanner
        suggestions={suggestions}
        onStartRegistration={handleStartRegistration}
      />

      <div className="t-flex t-flex-col t-gap-4">
        <NextDeadlineSection
          onReviewDueFilings={handleReviewDueFilings}
          onReviewOverdueFilings={handleReviewOverdueFilings}
        />
      </div>

      <div>
        <div className="t-flex t-items-center t-justify-between t-mb-3">
          <span className="t-text-subtext t-font-semibold t-text-text-100">
            Your states
          </span>
          <AddStateDropdown onSelectIntent={handleSelectAddStateIntent} />
        </div>

        <div className="t-flex t-items-center t-gap-2 t-mb-3">
          <Search
            placeholder="Search states..."
            defaultValue={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          <StateComplianceFilter
            filter={complianceFilter}
            updateFilter={updateComplianceFilter}
          />
        </div>

        <Table.Container className="sm:t-rounded-lg sm:t-border sm:t-border-solid sm:t-border-neutral-10 sm:t-px-4">
          <Table.Content>
            <Table.Head className="sm:t-hidden">
              <Table.HeadRow>
                <Table.HeadCell>State</Table.HeadCell>
                {/* <Table.HeadCell>Needs attention</Table.HeadCell> */}
                <Table.HeadCell className="t-w-1/4">
                  Filing deadline
                </Table.HeadCell>
                <Table.HeadCell>Status</Table.HeadCell>
                <Table.HeadCell>RA Status</Table.HeadCell>
                <Table.HeadCell>Action</Table.HeadCell>
              </Table.HeadRow>
            </Table.Head>
            <Table.Body>
              {stateRegistrations.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <div className="t-flex t-flex-col t-items-center t-justify-center t-py-10 t-gap-2">
                      <div className="t-text-subtext t-font-medium t-text-text-60">
                        No states found
                      </div>
                      <div className="t-text-body-sm t-text-text-30">
                        {showOverdueOnly ||
                        showDueOnly ||
                        searchTerm ||
                        complianceFilter.STATUS?.length
                          ? "Try adjusting your filters or search term."
                          : "Add a state to start tracking compliance."}
                      </div>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ) : (
                stateRegistrations.map((row) => (
                  <StateTableRow
                    key={row.state_id}
                    row={row}
                    nextDeadlineTask={getDeadlineForState(row.state)}
                    onRowClick={() => navigateToState(row.state_id)}
                    onLetInkleHandle={() => void handleMoveToInkle(row)}
                    isLetInkleHandleLoading={
                      moveToInkleLoadingStateId === row.state_id
                    }
                    onDeadlineClick={handleDeadlineClick}
                    onStartFiling={handleStartFiling}
                    isStartingFiling={checkingOut}
                    onStartRegistration={handleStartRegistration}
                    onViewCart={handleViewCart}
                    isMobile={isMobile}
                    onAddMissingInfo={(r) => {
                      setMissingInfoRow(r);
                      missingInfoModal.open();
                    }}
                  />
                ))
              )}
            </Table.Body>
          </Table.Content>
        </Table.Container>
      </div>

      <div>
        <div className="t-text-caption t-font-semibold t-uppercase t-tracking-wider t-text-text-30 t-mb-3">
          Other actions
        </div>
        <StateRegistrationActionCards
          generalHelpChannelUrl={generalHelpChannelUrl}
        />
      </div>

      <AddStateRegistrationModal
        key={addStateIntent ?? "no-intent"}
        {...addStateModal}
        close={() => {
          addStateModal.close();
          setAddStateIntent(null);
        }}
        initialIntent={addStateIntent ?? undefined}
      />

      {inkleHandleModal.isOpen && inkleHandleState && (
        <RegistrationOnboarding
          open={inkleHandleModal.isOpen}
          onClose={() => {
            inkleHandleModal.close();
            setInkleHandleState(null);
          }}
          stateData={inkleHandleState}
          entryPoint="banner"
          onIntakeComplete={handleIntakeComplete}
        />
      )}

      {rowInkleHandleModal.isOpen && rowInkleHandleState && (
        <RegistrationOnboarding
          open={rowInkleHandleModal.isOpen}
          onClose={() => {
            rowInkleHandleModal.close();
            setRowInkleHandleState(null);
          }}
          stateData={rowInkleHandleState}
          entryPoint="banner"
        />
      )}

      {checkoutModal.isOpen && Boolean(invoice) && (
        <CheckoutModal
          entityId={entityId!}
          type="invoices"
          onInvoicePaid={onPaymentSuccess}
          open={checkoutModal.isOpen}
          onClose={closeCheckoutModal}
          invoices={[invoice as BillingInvoice]}
          payerUserType={isForeignCA ? "FCA" : undefined}
        />
      )}

      {missingInfoModal.isOpen && missingInfoRow && (
        <MissingInfoModal
          isOpen={missingInfoModal.isOpen}
          close={() => {
            missingInfoModal.close();
            setMissingInfoRow(null);
          }}
          missingFields={getMissingFieldsForRow(missingInfoRow)}
          entityAttributes={entityAttributes}
          groupId={groupId!}
          entityId={entityId!}
          stateId={missingInfoRow.state_id}
        />
      )}
    </div>
  );
};
